import {
  Component,
  EventEmitter,
  Input,
  Output,
  booleanAttribute,
  inject,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { CardComponent } from '../../../../shared/components/card/card.component';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { TextWithIconComponent } from '../../../../shared/components/text-with-icon/text-with-icon.component';
import { ApiDeviceGroupResponseDto } from '@birco/rainmesh-api';
import { DeviceItemCardComponent } from '../device-item-card/device-item-card.component';
import { TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { RouteConstants } from '../../../../shared/constants/route-constants';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import { LabelConverter } from '../../../../shared/converter/label.converter';
import { isDeviceActive } from '../../utils/device-value.utils';
import {
  ContextMenuComponent,
  ContextMenuData,
} from '../../../../shared/components/context-menu/context-menu.component';
import { BehaviorSubject, map, of, switchMap } from 'rxjs';
import { DeviceResponseDto } from '../../models/device-response.interface';
import { selectSortedDevicesByGroupId } from '../../state/device-groups/device-group.selectors';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-area-item-card',
  imports: [
    CardComponent,
    IconComponent,
    TextWithIconComponent,
    DeviceItemCardComponent,
    TranslatePipe,
    TagComponent,
    ContextMenuComponent,
    AsyncPipe,
  ],
  templateUrl: './area-item-card.component.html',
  styleUrl: './area-item-card.component.scss',
})
export class AreaItemCardComponent {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly deviceGroupSubject =
    new BehaviorSubject<ApiDeviceGroupResponseDto | null>(null);
  protected readonly RouteConstants = RouteConstants;
  protected readonly LabelConverter = LabelConverter;

  readonly deviceGroupView$ = this.deviceGroupSubject.pipe(
    switchMap((deviceGroup) => {
      if (!deviceGroup) {
        return of(null);
      }

      return this.store
        .select(selectSortedDevicesByGroupId(deviceGroup.id))
        .pipe(
          map((devices: DeviceResponseDto[]) => ({
            deviceGroup,
            devices,
            deviceCount: devices.length,
            activeDeviceCount: devices.filter((device) =>
              isDeviceActive(device),
            ).length,
          })),
        );
    }),
  );

  private _deviceGroup?: ApiDeviceGroupResponseDto;
  @Input() isExpanded = false;
  @Input() set deviceGroup(value: ApiDeviceGroupResponseDto | undefined) {
    this._deviceGroup = value;
    this.deviceGroupSubject.next(value ?? null);
  }
  get deviceGroup(): ApiDeviceGroupResponseDto | undefined {
    return this._deviceGroup;
  }
  @Input() disabled: boolean = false;
  @Input() hideActions: boolean = false;
  @Input({ transform: booleanAttribute }) hideFavoriteIcon: boolean = false;
  @Output() isExpandedChange = new EventEmitter<boolean>();
  @Output() triggerDeviceGroupAction = new EventEmitter<{
    id: string;
    value: boolean;
  }>();

  contextMenu: ContextMenuData[] = [
    {
      labelKey: 'default.details',
      action: () => this.onDetails(),
    },
    {
      labelKey: 'deviceGroups.trigger.active',
      action: () => this.onTriggerDeviceGroupAction(true),
    },
    {
      labelKey: 'deviceGroups.trigger.inactive',
      action: () => this.onTriggerDeviceGroupAction(false),
    },
  ];

  onTriggerDeviceGroupAction(value: boolean) {
    if (!this.deviceGroup || this.disabled) return;
    this.triggerDeviceGroupAction.emit({
      id: this.deviceGroup.id,
      value: value,
    });
  }

  onToggleExpanded(event: MouseEvent): void {
    event.stopPropagation();
    this.isExpanded = !this.isExpanded;
    this.isExpandedChange.emit(this.isExpanded);
  }

  onDetails() {
    if (!this.deviceGroup?.id) return;
    void this.router.navigate([
      RouteConstants.routeDeviceGroup,
      this.deviceGroup.id,
    ]);
  }
}
